import { getUserContainer } from 'container/user-module'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import { LOGGED_USER_SERVICE_KEY } from 'modules/users'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './SchedulesTab.module.scss'
import { Box, Button, Skeleton } from '@mui/material'
import { getCalendarContainer } from 'container/calendar-module'
import { ExternAppointmentScheduleService } from 'modules/calendar/services/ExternAppointmentScheduleService'
import { EXTERN_APPOINTMENT_SCHEDULE_SERVICE_KEY } from 'modules/calendar'
import { ExternAppointmentSchedule } from 'modules/calendar/models/ExternAppointmentSchedule'
import AddIcon from '@mui/icons-material/Add'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { BoolQueryParam, Query, QueryParam } from 'common/api/Query'
import { SchedulesCalendar } from './ScheduleCalendar'
import { WeekDay } from 'modules/calendar/enums/WeekDay'
import { NewScheduleModal } from './NewScheduleModal'
import { toModel } from 'modules/calendar/models/ExternAppointmentScheduleDTO'
import { enqueueSnackbar } from 'notistack'

dayjs.extend(localizedFormat)
dayjs.extend(customParseFormat)
dayjs.locale('es')

const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const calendarContainer = getCalendarContainer()
const scheduleService = calendarContainer.get<ExternAppointmentScheduleService>(
  EXTERN_APPOINTMENT_SCHEDULE_SERVICE_KEY
)

export function SchedulesTab() {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [schedules, setSchedules] = useState<ExternAppointmentSchedule[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedSchedule, setSelectedSchedule] = useState<ExternAppointmentSchedule | undefined>(
    undefined
  )

  useEffect(() => {
    loadSchedules()
  }, [])

  const loadSchedules = () => {
    if (!loggedUser?.id) return

    setIsLoading(true)
    scheduleService
      .getFilteredList(
        new Query({
          query: [
            new QueryParam<ExternAppointmentSchedule>('userID', loggedUser.id),
            new QueryParam<ExternAppointmentSchedule>('isActive', new BoolQueryParam(true)),
          ],
        })
      )
      .subscribe({
        next: (response) => {
          setSchedules(response.items)
          setIsLoading(false)
        },
        error: (err) => {
          console.error('Error cargando horarios:', err)
          setErrorMessage(t('errorLoadingSchedules'))
          setIsLoading(false)
        },
      })
  }

  const handleOpenAddScheduleModal = () => {
    setOpenDialog(true)
  }

  const handleOpenNewScheduleModal = () => {
    setSelectedSchedule(undefined)
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  const handleOpenEditSchedule = (schedule: ExternAppointmentSchedule) => {
    setSelectedSchedule(schedule)
    handleOpenAddScheduleModal()
  }

  const handleSaveSchedule = (selectedDays: WeekDay[], value: number[]) => {
    if (!selectedSchedule) {
      handleAddSchedule(selectedDays, value)
      return
    }
    handleUpdateSchedule(selectedDays, value)
  }

  const handleAddSchedule = (selectedDays: WeekDay[], value: number[]) => {
    if (selectedDays.length < 1) {
      setErrorMessage(t('noDaysSelected'))
      return
    }

    if (!handleCheckSchedule(selectedDays, value)) return

    setErrorMessage('')

    const from = value[0] > 9 ? `${value[0]}:00` : `0${value[0]}:00`
    const to = value[1] > 9 ? `${value[1]}:00` : `0${value[1]}:00`

    const newSchedule: ExternAppointmentSchedule = toModel({
      userId: loggedUser?.id ?? '',
      from: from,
      to: to,
      days: selectedDays.map((value) => value as number).join(','),
    })

    scheduleService.add(newSchedule).subscribe({
      next: () => {
        loadSchedules()
        setOpenDialog(false)
      },
      error: () => {
        setErrorMessage(t('anErrorOccured'))
        setTimeout(() => setErrorMessage(''), 3000)
      },
    })
  }

  const handleUpdateSchedule = (selectedDays: WeekDay[], value: number[]) => {
    if (!handleCheckSchedule(selectedDays, value)) return

    setErrorMessage('')

    const from = value[0] > 9 ? `${value[0]}:00` : `0${value[0]}:00`
    const to = value[1] > 9 ? `${value[1]}:00` : `0${value[1]}:00`
    const isActive = selectedDays.length > 0
    const days = isActive
      ? selectedDays.map((value) => value as number).join(',')
      : selectedSchedule?.weekDays.map((value) => value as number).join(',') || ''

    const newSchedule: ExternAppointmentSchedule = toModel({
      id: selectedSchedule?.id ?? '',
      userId: loggedUser?.id ?? '',
      from: from,
      to: to,
      isActive: isActive,
      days: days,
    })

    scheduleService.update(selectedSchedule?.id ?? '', newSchedule).subscribe({
      next: () => {
        enqueueSnackbar(t('changesWereSaved'), { variant: 'success' })
        loadSchedules()
        handleCloseDialog()
      },
      error: () => {
        enqueueSnackbar(t('anErrorOccured'), { variant: 'error' })
        handleCloseDialog()
      },
    })
  }

  const handleCheckSchedule = (selectedDays: WeekDay[], value: number[]) => {
    const selectedStart = dayjs().hour(value[0]).minute(0).second(0)
    const selectedEnd = dayjs().hour(value[1]).minute(0).second(0)

    const isConflict = schedules
      .filter(
        (schedule) =>
          schedule !== selectedSchedule &&
          schedule.weekDays.some((day) => selectedDays.includes(day))
      )
      .some((schedule) => {
        const [fromHour, fromMin] = schedule.from.split(':').map(Number)
        const [toHour, toMin] = schedule.to.split(':').map(Number)

        const from = dayjs().hour(fromHour).minute(fromMin).second(0)
        const to = dayjs().hour(toHour).minute(toMin).second(0)

        const endsBefore = selectedEnd.isSame(from) || selectedEnd.isBefore(from)
        const startsAfter = selectedStart.isSame(to) || selectedStart.isAfter(to)

        return !(endsBefore || startsAfter)
      })

    if (!isConflict) {
      setErrorMessage('')
      return true
    } else {
      setErrorMessage(t('scheduleOccupied'))
      return false
    }
  }

  return (
    <Box className={styles.container}>
      {!isLoading ? (
        <>
          <SchedulesCalendar
            schedules={schedules}
            getSchedules={loadSchedules}
            handleOpenEditSchedule={handleOpenEditSchedule}
            selectedSchedule={selectedSchedule}
          />
          <Box className={styles.buttonContainer}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              className={styles.addButton}
              onClick={handleOpenNewScheduleModal}
            >
              {t('addSchedule')}
            </Button>
          </Box>
          <NewScheduleModal
            schedule={selectedSchedule}
            open={openDialog}
            handleSave={handleSaveSchedule}
            handleClose={handleCloseDialog}
            error={errorMessage}
            handleCheckSchedules={handleCheckSchedule}
          />
        </>
      ) : (
        <>
          <Box mb={3} mt={2}>
            <Skeleton width="100%" height={60} />
          </Box>
          <Box mb={4} sx={{ height: '350px' }}>
            <Skeleton width="100%" height="100%" />
          </Box>
          <Box className={styles.buttonContainer}>
            <Skeleton width="120px" height={40} />
            <Skeleton width="120px" height={40} sx={{ ml: 2 }} />
          </Box>
        </>
      )}
    </Box>
  )
}
